import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { uploadFile } from '../../services/storageService';
import { submitApkForAnalysis, triggerApkAnalysis } from '../../services/analysisUIService';
import { auth } from '../../config/firebase';
import { addDocument, queryDocuments, updateDocument } from '../../services/dbService';
import InternalMenu from '../../components/shared/InternalMenu';
import Button from '../../components/Button';
import { AppStatus, AppStatusInfo } from '../../constants/appStatus';
import { serverTimestamp } from 'firebase/firestore';
import { updateAppStatus } from '../../services/appStatusService';

const DashboardView = ({ projects, loading }) => {
  // Rotating words for banner animation
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const words = ["Analyze,  ", " Deploy,  ", " Optimize,  ", " Succeed! "];
  
  // Animation effect for rotating words
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prevIndex) => {
        // Stop incrementing when we reach the last word
        if (prevIndex < words.length - 1) {
          return prevIndex + 1;
        } else {
          // Clear the interval when reaching the end
          clearInterval(interval);
          return prevIndex;
        }
      });
    }, 2000); // Change word every 2 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Dashboard menu items with icons - using lighter stroke color for icons
  const menuItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard',
      icon: <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    },
    { 
      path: '/new-project', 
      label: 'New Project',
      icon: <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-h-screen bg-black">
      {/* Fixed Sidebar */}
      <InternalMenu 
        items={menuItems} 
        variant="dashboard" 
        showUserProfile={true} 
      />
      
      {/* Main Content Area (takes remaining width) */}
      <main className="flex-1 md:ml-64 w-full text-text-white overflow-y-auto">
        {/* Header Border Line */}
        <div className="w-full h-[1px] bg-[#e9ecef]/10"></div>

        {/* Banner inside the main content area - adjusted for full width */}
        <div className="w-full pt-8 pb-4">
          <div className="relative mx-4 md:mx-8 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 rounded-2xl overflow-hidden shadow-xl">
            {/* Left code icon - replaced with Google Play icon */}
            <div className="absolute left-6 bottom-0 transform -rotate-6 hidden sm:block md:block">
              <div className="bg-blue-900 rounded-xl w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 shadow-lg flex items-center justify-center transform rotate-12">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-cyan-300" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.609 22.186c-.181.181-.29.432-.29.7 0 .256.097.49.29.683.181.181.427.272.683.272.256 0 .502-.09.683-.272l10.675-10.675c.378-.378.378-.995 0-1.373L4.975 1.152C4.793.97 4.547.879 4.292.879c-.256 0-.502.09-.683.272-.376.378-.376.995 0 1.373zm7.247-.768L21.064 11.254c.18.18.279.423.279.679s-.098.498-.28.698L10.856 22.954c-.18.18-.423.28-.698.28s-.519-.101-.699-.28c-.18-.18-.279-.423-.279-.698s.098-.519.28-.698l9.758-9.758-9.76-9.758c-.18-.18-.279-.423-.279-.698s.098-.519.28-.698c.359-.359.996-.359 1.356 0z"/>
                </svg>
              </div>
            </div>
            
            {/* Right code icon - replaced with Apple Store icon */}
            <div className="absolute right-6 bottom-0 transform rotate-6 hidden sm:block md:block">
              <div className="bg-blue-900 rounded-xl w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 shadow-lg flex items-center justify-center transform -rotate-12">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-cyan-300" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
            </div>
            
            <div className="px-8 py-8 sm:py-10 md:py-12 text-center relative z-10">
              <div className="h-10 sm:h-12 md:h-16 relative flex justify-center items-center">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                  {words.slice(0, currentWordIndex + 1).join(" ")}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Applications List or Upload Area */}
        <div className="px-4 md:px-8 mt-8 pb-12">
          {projects.length > 0 ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Your Applications</h2>
              </div>
              
              {/* Flex container for 70-30 split */}
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left side - Applications (70%) */}
                <div className="w-full md:w-[70%]">
                  {/* Application Cards */}
                  <div className="space-y-4">
                    {projects.map((app) => (
                      <div key={app.id} className="w-full bg-[#111] border-b border-gray-800 py-6 px-6 rounded-xl transition-all hover:shadow-sm">
                        {/* Desktop layout */}
                        <div className="hidden md:flex items-center justify-between">
                          {/* Left section - App icon and name */}
                          <div className="flex items-center space-x-4 w-[200px] min-w-[200px]">
                            <div className="relative w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center shrink-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {(app.versionCount || 1) > 1 && (
                                <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                  {app.versionCount}
                                </span>
                              )}
                            </div>
                            <div className="overflow-hidden">
                              <h3 className="font-semibold text-white text-lg truncate">{app.fileName}</h3>
                              {(app.versionCount || 1) > 1 && (
                                <span className="text-xs text-gray-400">v{app.versionCount} • {app.versionCount} versions</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Middle section - Status and progress */}
                          <div className="flex-grow px-10 max-w-md">
                            <div className="flex items-center space-x-2 mb-2">
                              {/* Status text with appropriate color */}
                              <span className={`text-sm font-medium text-${AppStatusInfo[app.statusApp]?.color || 'gray'}-400 w-[120px]`}>
                                {/* Display status name */}
                                {AppStatusInfo[app.statusApp]?.displayName || 'Unknown Status'}
                              </span>
                              
                              {/* Percentage complete */}
                              <span className="text-sm text-gray-400 w-[40px] text-right">
                                {app.percentComplete || AppStatusInfo[app.statusApp]?.progressPercent || 0}%
                              </span>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="w-full bg-gray-700 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full bg-${AppStatusInfo[app.statusApp]?.color || 'gray'}-500`}
                                style={{ width: `${app.percentComplete || AppStatusInfo[app.statusApp]?.progressPercent || 0}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Right section - Dates and actions */}
                          <div className="flex items-center justify-end space-x-6">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">
                                Uploaded: {app.uploadedAt ? 
                                  (typeof app.uploadedAt.toDate === 'function' 
                                    ? new Date(app.uploadedAt.toDate()).toLocaleDateString('en-US') 
                                    : new Date(app.uploadedAt).toLocaleDateString('en-US')
                                  ) 
                                  : 'Not Available'}
                              </div>
                              {app.lastUpdated && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Updated: {typeof app.lastUpdated.toDate === 'function'
                                    ? new Date(app.lastUpdated.toDate()).toLocaleDateString('en-US')
                                    : new Date(app.lastUpdated).toLocaleDateString('en-US')}
                                </div>
                              )}
                            </div>
                            
                            <Link 
                              to={`/app-details/${app.fileId}`}
                              className="px-4 py-1.5 border border-gray-600 text-sm font-medium text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                        
                        {/* Mobile layout */}
                        <div className="md:hidden">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3 max-w-[60%]">
                              <div className="relative w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {(app.versionCount || 1) > 1 && (
                                  <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {app.versionCount}
                                  </span>
                                )}
                              </div>
                              <div className="overflow-hidden">
                                <h3 className="font-semibold text-white truncate">{app.fileName}</h3>
                                {(app.versionCount || 1) > 1 && (
                                  <span className="text-xs text-gray-400">v{app.versionCount}</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Status badge in mobile layout */}
                            <span className={`text-xs font-medium px-2 py-1 rounded-full bg-${AppStatusInfo[app.statusApp]?.color || 'gray'}-900 text-${AppStatusInfo[app.statusApp]?.color || 'gray'}-300 whitespace-nowrap ml-2`}>
                              {AppStatusInfo[app.statusApp]?.displayName?.split(' ')[0] || 'Unknown'}
                            </span>
                          </div>

                          {/* Add progress bar to mobile layout */}
                          <div className="mb-3">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs text-gray-400">
                                {app.percentComplete || AppStatusInfo[app.statusApp]?.progressPercent || 0}%
                              </span>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="w-full bg-gray-700 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full bg-${AppStatusInfo[app.statusApp]?.color || 'gray'}-500`}
                                style={{ width: `${app.percentComplete || AppStatusInfo[app.statusApp]?.progressPercent || 0}%` }}
                              />
                            </div>
                          </div>

                          {/* Add dates and action button to mobile layout */}
                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-800">
                            <div>
                              <div className="text-xs text-gray-400">
                                Uploaded: {app.uploadedAt ? 
                                  (typeof app.uploadedAt.toDate === 'function' 
                                    ? new Date(app.uploadedAt.toDate()).toLocaleDateString('en-US') 
                                    : new Date(app.uploadedAt).toLocaleDateString('en-US')
                                  ) 
                                  : 'Not Available'}
                              </div>
                              {app.lastUpdated && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Updated: {typeof app.lastUpdated.toDate === 'function'
                                    ? new Date(app.lastUpdated.toDate()).toLocaleDateString('en-US')
                                    : new Date(app.lastUpdated).toLocaleDateString('en-US')}
                                </div>
                              )}
                            </div>
                            
                            <Link 
                              to={`/app-details/${app.fileId}`}
                              className="px-3 py-1 border border-gray-600 text-xs font-medium text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right side - New Project Card (30%) */}
                <div className="w-full md:w-[30%]">
                  <Link to="/new-project" className="block h-full">
                    <div className="bg-[#111] border border-gray-700 h-[300px] rounded-xl shadow-md p-6 flex flex-col items-center justify-center transition-transform transform hover:scale-105">
                      <img 
                        src="/images/new_project.jpeg" 
                        alt="New Project" 
                        className="w-full h-auto mb-4 rounded-lg"
                      />
                      <h3 className="text-white text-xl font-bold mb-2">New Project</h3>
                      <p className="text-gray-400 text-center">Upload a new AAB file for analysis</p>
                    </div>
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center py-16">
              <div className="max-w-md w-full">
                <Link to="/new-project" className="block">
                  <div className="bg-[#111] border border-gray-700 h-[350px] rounded-xl shadow-md p-8 flex flex-col items-center justify-center transition-transform transform hover:scale-105">
                    <img 
                      src="/images/new_project.jpeg" 
                      alt="New Project" 
                      className="w-full h-auto mb-6 rounded-lg"
                    />
                    <h3 className="text-white text-2xl font-bold mb-3">Create New Project</h3>
                    <p className="text-gray-400 text-center mb-6">Start by uploading your first AAB file for analysis</p>
                    <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white py-3 px-6 rounded-lg font-medium">
                      Get Started
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// A simple dropzone component for the dashboard
const DashboardDropzone = () => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [fileId, setFileId] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const uploadTaskRef = useRef(null);
  const navigate = useNavigate();
  
  // Cancel upload if component unmounts during upload
  useEffect(() => {
    return () => {
      if (uploadTaskRef.current) {
        uploadTaskRef.current.cancel();
      }
    };
  }, []);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = () => {
    setDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.aab')) {
      handleFileSelected(file);
    } else {
      setError('Only AAB files are supported');
    }
  };
  
  const handleFileSelected = (file) => {
    // Clear previous errors
    setError('');
    setUploading(true);
    setProgress(0);
    
    // Generate a unique file ID for analysis reference
    const uniqueFileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setFileId(uniqueFileId);
    setFileName(file.name);
    
    // Format file size for display
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };
    
    const fileSize = formatFileSize(file.size);
    
    // Upload the file
    uploadTaskRef.current = uploadFile(
      file,
      (progressValue) => {
        setProgress(progressValue);
      },
      async (downloadUrl) => {
        try {
          // Submit APK for analysis
          const user = auth.currentUser;
          if (!user) {
            // Save file data to session storage and redirect to login
            sessionStorage.setItem('redirectAfterLogin', '/dashboard');
            navigate('/login');
            return;
          }
          
          console.log('Uploading file in DashboardDropzone:', file.name, 'User:', user.uid);
          
          // Add to userApps collection first
          let userAppDocId;
          try {
            // Use JavaScript Date for immediate data availability
            const currentDate = new Date();
            
            // Always create a new document. After analysis completes, the cloud function
            // will detect same Application ID (packageName) and merge versions automatically.
            userAppDocId = await addDocument('userApps', {
              fileId: uniqueFileId,
              fileName: file.name,
              fileSize: fileSize,
              downloadUrl: downloadUrl,
              userId: user.uid,
              uploadedAt: currentDate,
              statusApp: AppStatus.ANALYZING_APK,
              lastStatusUpdate: currentDate,
              versionCount: 1,
              versionFileIds: [uniqueFileId],
              versions: [{
                fileId: uniqueFileId,
                fileSize: fileSize,
                downloadUrl: downloadUrl,
                uploadedAt: currentDate.toISOString(),
                versionNumber: 1,
                statusApp: AppStatus.ANALYZING_APK
              }],
              statusHistory: [
                {
                  status: AppStatus.AWAITING_UPLOAD,
                  timestamp: currentDate,
                  notes: 'File uploaded successfully'
                },
                {
                  status: AppStatus.ANALYZING_APK,
                  timestamp: currentDate,
                  notes: 'Analysis started immediately after upload'
                }
              ],
              appType: 'aab'
            });
            console.log('AAB file from dashboard linked to user in Firestore with ID:', userAppDocId, 'Status: ANALYZING_APK');
            
          } catch (error) {
            console.error('Error linking dashboard AAB to user:', error);
            alert('אפליקציה הועלתה אך לא נוספה לרשימת האפליקציות שלך. אנא רענן את העמוד.');
            // Continue anyway with the analysis
          }
          
          // Submit the APK for analysis
          const result = await submitApkForAnalysis(uniqueFileId, file.name, downloadUrl);
          
          // כבר לא צריך לעדכן את הסטטוס כי קבענו אותו ל-ANALYZING_APK מלכתחילה
          // Trigger the server-side analysis function
          await triggerApkAnalysis(uniqueFileId);
          
          // Set uploading to false and reset to show new upload option
          setUploading(false);
          setFileId('');
          setFileName('');
          
          // Force reload the current page to show new app
          window.location.reload();

        } catch (analysisError) {
          console.error('Error submitting analysis:', analysisError);
          setError(`Error starting analysis: ${analysisError.message}`);
          setUploading(false);
        }
      },
      (errorMsg) => {
        setUploading(false);
        setError(`Upload error: ${errorMsg}`);
      },
      uniqueFileId
    );
  };
  
  const handleBrowseFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return (
    <div 
      className={`bg-bg-dark p-8 rounded-lg border-2 border-dashed ${dragOver ? 'border-primary bg-bg-darker' : 'border-bg-dark'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input 
        type="file"
        ref={fileInputRef}
        accept=".aab"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelected(file);
          }
        }}
      />
      
      {error && (
        <div className="bg-red-900 text-white p-4 rounded-lg mb-6">
          {error}
        </div>
      )}
      
      {uploading ? (
        <div className="text-center">
          <div className="relative pt-1">
            <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-bg-dark">
              <div
                style={{ width: `${progress}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary transition-all duration-300"
              />
            </div>
          </div>
          <p className="text-xl text-text-gray-light mt-4 text-center">Uploading... {progress}%</p>
        </div>
      ) : (
        <div className="text-center cursor-pointer" onClick={handleBrowseFiles}>
          <div className="w-20 h-20 mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-primary">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          
          <h3 className="text-2xl font-medium mb-4">Drag and drop AAB here</h3>
          <p className="text-text-gray-light mb-6">Or click to select a file from your computer</p>
          
          <button className="inline-flex items-center bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-lg transition duration-200 font-medium">
            <span>Select AAB File</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardView; 